import React, { useMemo, useState } from 'react';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { Box, Typography, Paper, Fade, Popper } from '@mui/material';

const StockLable = ({ data = [], currentSelection }) => {
    const [anchorEl, setAnchorEl] = useState(null);

    const stats = useMemo(() => {
        // Initial state
        const acc = { 
            current: { zero: 0, low: 0 }, 
            others: { zero: 0, low: 0 }, 
            total: 0 
        };

        if (!Array.isArray(data)) return acc;

        data.forEach((selection) => {
            const isCurrent = Number(selection.id) === Number(currentSelection);
            
            selection.rows?.forEach((row) => {
                // Ensure we have a valid product
                if (!row.productCode) return;

                const stock = Number(row.stock) || 0;
                const qty = Number(row.quantity) || 0;

                // Logic: Issue if stock is 0 or less than requested quantity
                if (stock === 0 || stock < qty) {
                    const target = isCurrent ? acc.current : acc.others;
                    
                    if (stock === 0) {
                        target.zero += 1;
                    } else {
                        target.low += 1;
                    }
                    acc.total += 1;
                }
            });
        });

        return acc;
    }, [data, currentSelection]);

    // Don't show anything if there are no stock issues at all
    if (stats.total === 0) return null;

    const hasCurrentIssues = stats.current.zero > 0 || stats.current.low > 0;
    const open = Boolean(anchorEl);

    return (
        <Box
            onMouseEnter={(e) => setAnchorEl(e.currentTarget)}
            onMouseLeave={() => setAnchorEl(null)}
            sx={{
                position: 'relative',
                display: 'inline-flex',
                alignItems: 'center',
            }}
        >
            <Popper
                open={open}
                anchorEl={anchorEl}
                placement="top-end"
                transition
                sx={{ zIndex: 1400 }}
                modifiers={[{ name: 'offset', options: { offset: [0, 12] } }]}
            >
                {({ TransitionProps }) => (
            <Fade {...TransitionProps} timeout={250}>
                <Paper
                    elevation={4}
                    sx={{
                        borderRadius: '4px',
                        minWidth: '240px',
                        bgcolor: '#ffffff',
                        border: '2px solid #0f172a',
                        overflow: 'hidden',
                        pointerEvents: 'none' 
                    }}
                >
                    <Box sx={{ p: 1.5, bgcolor: '#0f172a' }}>
                        <Typography sx={{ color: '#ffffff', fontSize: '11px', fontWeight: 900, letterSpacing: '1px' }}>
                            STOCK WARNING
                        </Typography>
                    </Box>

                    <Box sx={{ p: 2 }}>
                        <Box sx={{ mb: 2.5 }}>
                            <Typography sx={{ color: '#0f172a', fontSize: '10px', fontWeight: 800, mb: 1.5, borderBottom: '1px solid #e2e8f0', pb: 0.5 }}>
                                CURRENT SELECTION
                            </Typography>
                            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                                <Box sx={{ p: 1.5, borderRadius: '2px', border: '1px solid #9a3412', bgcolor: '#fecaca' }}>
                                    <Typography sx={{ fontSize: '9px', fontWeight: 900, color: '#420706' }}>ZERO STOCK</Typography>
                                    <Typography sx={{ fontSize: '20px', fontWeight: 900, color: '#000000', lineHeight: 1 }}>{stats.current.zero}</Typography>
                                </Box>
                                <Box sx={{ p: 1.5, borderRadius: '2px', border: '1px solid #a16207', bgcolor: '#fef08a' }}>
                                    <Typography sx={{ fontSize: '9px', fontWeight: 900, color: '#431407' }}>LOW STOCK</Typography>
                                    <Typography sx={{ fontSize: '20px', fontWeight: 900, color: '#000000', lineHeight: 1 }}>{stats.current.low}</Typography>
                                </Box>
                            </Box>
                        </Box>

                        {(stats.others.zero > 0 || stats.others.low > 0) && (
                            <Box>
                                <Typography sx={{ color: '#0f172a', fontSize: '10px', fontWeight: 800, mb: 1, borderBottom: '1px solid #e2e8f0', pb: 0.5 }}>
                                    OTHER SELECTIONS
                                </Typography>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', bgcolor: '#f8fafc', p: 1, borderRadius: '2px' }}>
                                    <Box>
                                        <Typography sx={{ color: '#475569', fontSize: '9px', fontWeight: 700 }}>ZERO</Typography>
                                        <Typography sx={{ color: '#000000', fontSize: '14px', fontWeight: 900 }}>{stats.others.zero}</Typography>
                                    </Box>
                                    <Box sx={{ textAlign: 'right' }}>
                                        <Typography sx={{ color: '#475569', fontSize: '9px', fontWeight: 700 }}>LOW</Typography>
                                        <Typography sx={{ color: '#000000', fontSize: '14px', fontWeight: 900 }}>{stats.others.low}</Typography>
                                    </Box>
                                </Box>
                            </Box>
                        )}
                    </Box>
                </Paper>
            </Fade>
            )}
            </Popper>

            <Box
                sx={{
                    width: 43,
                    height: 43,
                    bgcolor: hasCurrentIssues ? '#ef4444' : '#0f172a',
                    color: '#ffffff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    borderRadius: '50%',
                    border: '2px solid #000000',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    position: 'relative',
                    '&:hover': {
                        transform: 'translateY(-4px)',
                        bgcolor: hasCurrentIssues ? '#b91c1c' : '#1e293b',
                    }
                }}
            >
                <WarningAmberRoundedIcon sx={{ fontSize: 25, color: hasCurrentIssues ? '#ffffff' : '#fbbf24' }} />
                <Box
                    sx={{
                        position: 'absolute',
                        top: -10,
                        right: -10,
                        bgcolor: '#000000',
                        color: '#ffffff',
                        minWidth: '22px',
                        height: '22px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '11px',
                        fontWeight: 900,
                        px: 0.8,
                        borderRadius: '4px',
                        border: '2px solid #ffffff'
                    }}
                >
                    {stats.total}
                </Box>
            </Box>
        </Box>
    );
};

export default StockLable;